/**
 * TopDownMovementComponent - panning and zoom for overhead camera rigs.
 * Interprets inputs as planar movement with vertical zoom. For strategy/editor cameras.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { BasePawnMovementComponent } from './BasePawnMovementComponent.js';

import type { SceneComponentOptions } from '../SceneComponent.js';

// Define configuration options for top-down movement
export interface TopDownMovementOptions extends SceneComponentOptions {
  /** Panning speed in units per second */
  panSpeed: number;
  /** Zoom speed in units per scroll tick */
  zoomSpeed: number;
  /** Invert panning */
  invertPan: boolean;
}

// Component that handles top-down movement for pawns
@EngineClass('TopDownMovementComponent')
export class TopDownMovementComponent extends BasePawnMovementComponent {


  @property()
  public panSpeed: number = 10;

  @property()
  public zoomSpeed: number = 5;

  @property()
  public invertPan: boolean = false;

  public override initialize(options?: TopDownMovementOptions): void {
    super.initialize(options);
    this.panSpeed = options?.panSpeed ?? this.panSpeed;
    this.zoomSpeed = options?.zoomSpeed ?? this.zoomSpeed;
    this.invertPan = options?.invertPan ?? this.invertPan;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    const owner = this.getActor()!;

    const panInputY = -this.forwardInput.value * (this.invertPan ? -1 : 1);
    const panInputX = this.rightInput.value * (this.invertPan ? -1 : 1);

    if (panInputX || panInputY) {
      var deltaPosition = new THREE.Vector3(0, 0, 0);
      deltaPosition.add(new THREE.Vector3(panInputX, 0, panInputY).multiplyScalar(this.panSpeed * deltaTime));
      deltaPosition.multiplyScalar(this.speedModifier);
      owner.rootComponent.addWorldPosition(deltaPosition);
    }

    if (this.zoomInput.value !== 0) {
      const zoomChange = this.zoomInput.value * this.zoomSpeed * deltaTime;
      owner.rootComponent.addWorldPosition(new THREE.Vector3(0, zoomChange, 0).multiplyScalar(this.speedModifier));
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_TopDownMovement';
  }
}
